import { useCallback, useEffect, useRef, useState } from 'react';
import AutoScroll from 'embla-carousel-auto-scroll';
import Autoplay from 'embla-carousel-autoplay';

import type { SortableCollectionListBaseProps } from '@/components/blocks/editor/collection/collection-dnd';
import {
  Carousel,
  type CarouselApi,
  CarouselContent,
  CarouselItem,
  CarouselNavControls,
} from '@/components/ui/carousel';
import type { CollectionItem, SymmetricCarouselDisplay } from '@/lib/blocks/collection/blocks';
import {
  getCarouselContentClassName,
  getCarouselContentPaddingClassName,
  getCarouselItemClassName,
  getVerticalCarouselHeightClassName,
  repeatItemsForAutoScroll,
} from '@/lib/blocks/collection/blocks';
import type { CollectionMediaLayout } from '@/lib/blocks/collection/media-layout';
import { cn, getDocument } from '@/lib/utils';
import { useCollectionReorderVisual } from '../collection-reorder-visual-context';
import { SortableCollectionCard } from './collection-card';
import { MarqueePauseContext } from './use-marquee-pause';

interface SortableCarouselListProps extends SortableCollectionListBaseProps {
  mediaLayout: CollectionMediaLayout;
  carouselDisplay: SymmetricCarouselDisplay;
}

export function SortableCarouselList({
  items,
  cardConfig,
  mediaLayout,
  cardPaddingClass,
  cardStyle,
  carouselDisplay,
  collectionSortableGroup,
  collectionItemDragData,
  disabled,
  actionsDisabled,
  selectedItemId,
  onUpdate,
  onDelete,
  onDuplicate,
  onSelectionChange,
}: SortableCarouselListProps) {
  const [carouselApi, setCarouselApi] = useState<CarouselApi>();
  const [isFieldEditing, setIsFieldEditing] = useState(false);
  const [editorPauseCount, setEditorPauseCount] = useState(0);
  const isCarouselPaused = isFieldEditing || editorPauseCount > 0;
  const isCarouselPausedRef = useRef(false);
  isCarouselPausedRef.current = isCarouselPaused;
  const carouselApiRef = useRef<CarouselApi>(undefined);
  const scrollDirectionRef = useRef<'prev' | 'next' | null>(null);
  const { reorderDragActive } = useCollectionReorderVisual();
  const reorderDragActiveRef = useRef(false);
  reorderDragActiveRef.current = reorderDragActive;
  const handleEditorPauseChange = useCallback((active: boolean) => {
    setEditorPauseCount((count) => (active ? count + 1 : Math.max(0, count - 1)));
  }, []);

  useEffect(() => {
    carouselApiRef.current = carouselApi;
    if (!carouselApi) return;
    const onReInit = () => {
      if (!isCarouselPausedRef.current && !reorderDragActiveRef.current) return;
      const plugins = carouselApi.plugins() as unknown as Record<
        string,
        { stop(): void; play(): void } | undefined
      >;
      plugins.autoScroll?.stop();
      plugins.autoplay?.stop();
    };
    carouselApi.on('reInit', onReInit);
    return () => {
      carouselApi.off('reInit', onReInit);
    };
  }, [carouselApi]);

  useEffect(() => {
    const api = carouselApiRef.current;
    if (!api) return;
    const p = api.plugins() as unknown as Record<
      string,
      { stop(): void; play(): void } | undefined
    >;
    if (isCarouselPaused || reorderDragActive) {
      p.autoScroll?.stop();
      p.autoplay?.stop();
    } else {
      p.autoScroll?.play();
      p.autoplay?.play();
    }
  }, [isCarouselPaused, reorderDragActive]);

  // Scroll carousel toward pointer edge while a card drag is active
  useEffect(() => {
    if (!reorderDragActive) {
      scrollDirectionRef.current = null;
      return;
    }

    const isVertical = carouselDisplay.orientation === 'vertical';
    const SCROLL_ZONE_PX = 80;

    const handlePointerMove = (e: PointerEvent) => {
      const api = carouselApiRef.current;
      if (!api) return;
      const rect = api.rootNode().getBoundingClientRect();
      const pos = isVertical ? e.clientY : e.clientX;
      const start = isVertical ? rect.top : rect.left;
      const end = isVertical ? rect.bottom : rect.right;
      if (pos < start + SCROLL_ZONE_PX) {
        scrollDirectionRef.current = 'prev';
      } else if (pos > end - SCROLL_ZONE_PX) {
        scrollDirectionRef.current = 'next';
      } else {
        scrollDirectionRef.current = null;
      }
    };

    const intervalId = setInterval(() => {
      const api = carouselApiRef.current;
      const dir = scrollDirectionRef.current;
      if (!api || !dir) return;
      if (dir === 'prev') api.scrollPrev();
      else api.scrollNext();
    }, 650);

    getDocument()?.addEventListener('pointermove', handlePointerMove);
    return () => {
      getDocument()?.removeEventListener('pointermove', handlePointerMove);
      clearInterval(intervalId);
      scrollDirectionRef.current = null;
    };
  }, [reorderDragActive, carouselDisplay.orientation]);

  const carouselConfig = {
    itemsPerView: {
      mobile: carouselDisplay.itemsPerView.mobile as 1 | 2 | 3 | 4 | 5 | 6,
      tablet: carouselDisplay.itemsPerView.tablet as 1 | 2 | 3 | 4 | 5 | 6,
      desktop: carouselDisplay.itemsPerView.desktop as 1 | 2 | 3 | 4 | 5 | 6,
    },
    gap: carouselDisplay.gap,
    arrows: carouselDisplay.arrows,
    orientation: carouselDisplay.orientation,
    navPosition: carouselDisplay.navPosition,
    navButtonStyle: carouselDisplay.navButtonStyle,
  };

  const renderCard = (item: CollectionItem, sortableId?: string) => {
    const sortIndex = items.findIndex((i) => i.id === item.id);
    const sortIndexSafe = sortIndex >= 0 ? sortIndex : 0;
    return (
      <SortableCollectionCard
        item={item}
        totalCount={items.length}
        sortIndex={sortIndexSafe}
        collectionSortableGroup={collectionSortableGroup}
        collectionItemDragData={collectionItemDragData}
        cardConfig={cardConfig}
        mediaLayout={mediaLayout}
        sideBySideMode="carousel"
        paddingClassName={cardPaddingClass}
        contentPaddingClassName={getCarouselContentPaddingClassName(carouselDisplay)}
        cardStyle={cardStyle}
        disabled={disabled}
        actionsDisabled={actionsDisabled}
        onUpdate={onUpdate}
        onDelete={() => onDelete(item.id)}
        onDuplicate={() => onDuplicate(item.id)}
        primarySelected={selectedItemId === item.id}
        onSelectionChange={(selected) => onSelectionChange(item.id, selected)}
        sortableId={sortableId}
      />
    );
  };

  const isVertical = carouselDisplay.orientation === 'vertical';
  const useAutoScroll = isVertical || carouselDisplay.autoScroll;
  const hasAutoplay = !useAutoScroll && !carouselDisplay.arrows;
  const needsPauseProvider = useAutoScroll || hasAutoplay;
  const orientation = isVertical ? 'vertical' : ('horizontal' as const);

  const loopEnabled =
    useAutoScroll || !carouselDisplay.arrows || items.length > carouselDisplay.itemsPerView.desktop;
  const displayItems = loopEnabled
    ? repeatItemsForAutoScroll(items, carouselDisplay.itemsPerView.desktop)
    : items;

  const plugins = useAutoScroll
    ? [
        AutoScroll({
          speed: isVertical ? 0.9 : 0.7,
          startDelay: 0,
          stopOnMouseEnter: false,
          stopOnInteraction: true,
        }),
      ]
    : hasAutoplay
      ? [Autoplay({ delay: 3000, stopOnInteraction: true })]
      : [];

  const opts = useAutoScroll
    ? {
        align: 'start' as const,
        containScroll: false as const,
        loop: true,
        dragFree: true,
        watchDrag: false,
      }
    : {
        align: 'start' as const,
        containScroll: 'trimSnaps' as const,
        loop: loopEnabled,
        watchDrag: false,
      };

  const carouselEl = (
    <Carousel
      orientation={orientation}
      className={isVertical ? 'h-full w-full' : 'w-full'}
      setApi={setCarouselApi}
      onMouseEnter={needsPauseProvider ? () => setIsFieldEditing(true) : undefined}
      onMouseLeave={needsPauseProvider ? () => setIsFieldEditing(false) : undefined}
      opts={opts}
      plugins={plugins}
    >
      <CarouselContent
        className={getCarouselContentClassName(carouselDisplay.gap, orientation)}
        viewportClassName="p-[2px] -m-[2px]"
      >
        {displayItems.map((item, index) => {
          const isDuplicate =
            loopEnabled && index !== displayItems.findIndex((c) => c.id === item.id);
          const sortableId = isDuplicate ? `${item.id}:dup:${index}` : undefined;
          return (
            <CarouselItem
              key={loopEnabled ? `${item.id}-${index}` : item.id}
              className={cn(getCarouselItemClassName(carouselConfig))}
            >
              {renderCard(item, sortableId)}
            </CarouselItem>
          );
        })}
      </CarouselContent>
      {!useAutoScroll && carouselDisplay.arrows ? (
        <CarouselNavControls
          navPosition={carouselDisplay.navPosition}
          navButtonStyle={carouselDisplay.navButtonStyle}
          className={cardPaddingClass?.replace(/^p-/, 'pl-')}
        />
      ) : null}
    </Carousel>
  );

  const wrappedCarousel = needsPauseProvider ? (
    <MarqueePauseContext.Provider value={handleEditorPauseChange}>
      {carouselEl}
    </MarqueePauseContext.Provider>
  ) : (
    carouselEl
  );

  return isVertical ? (
    <div className={getVerticalCarouselHeightClassName(carouselDisplay.verticalHeight)}>
      {wrappedCarousel}
    </div>
  ) : (
    wrappedCarousel
  );
}
